// src/components/customer/Tabs/ProductsTab.tsx

import React from "react";

import CategorySlideshow from "../CategorySlideshow";
import ProductCarouselSection from "../ProductCarouselSection";
import PeekCarousel from "../PeekCarousel";
import StripBanner from "../StripBanner";
import TopBrandsSection from "../TopBrandsSection";
import PetPromoBanner from "../PetPromoBanner";
import WavesSection from "../WavesSection";
import MultiBanner from "../MultiBanner";
import PetBanner from "../PetBanner";
import VideoCarousel from "../VideoCarousel";
import FeatureHighlights from "../FeatureHighlights";
import { CategorySlider } from "../CategorySlider";
import Carousel from "../Carousel";
import SectionHeader from "../SectionHeader";
import GoogleReviewsCarousel from "../GoogleReviews";

interface ProductsTabProps {
  orderedSections: any[];
  data: any;
  handlers: any;
  saverSaleRef: React.RefObject<HTMLDivElement | null>;
}

export const ProductsTab: React.FC<ProductsTabProps> = ({
  orderedSections,
  data,
  handlers,
  saverSaleRef,
}) => {
  // =========================================================
  // PRODUCT CAROUSEL WITH OFFER
  // =========================================================

  const renderProductCarouselWithOffer = (s: any) => {
    const rawProducts = s?.data?.length ? s.data : data.JoseraFinal;

    const mappedProducts = rawProducts?.map((p: any) => ({
      ProductId: p.productId ?? p.ProductId,

      Name: p.name ?? p.Name,

      ImageUrl: p.imageUrl ?? p.ImageUrl,

      Price: p.price ?? p.Price,

      salePrice: p.salePrice ?? p.SalePrice,

      InStock: p.inStock ?? p.InStock ?? true,

      RemainingSaleSeconds:
        p.RemainingSaleSeconds ?? p.remainingSaleSeconds ?? 0,

      ItemType: p.ItemType ?? p.itemType,

      RedirectUrl: p.RedirectUrl ?? p.redirectUrl,

      MarketingTexts: p.MarketingTexts ?? p.marketingTexts ?? [],

      Rating: p.Rating ?? p.rating ?? 0,

      // IMPORTANT
      Images: p.Images ?? p.images ?? [],
    }));

    return (
      <ProductCarouselSection
        showLogo={false}
        title={
          <span className="flex items-center gap-2 text-logoHeading text-base md:text-xl font-bold">
            Weekly Best Sellers:
          </span>
        }
        handleProductPage={handlers.onProductClick}
        products={mappedProducts}
        headingPosition="left"
        handleViewAllClick={handlers.onViewAllFlashDealsClick}
        handleCheckoutClick={handlers.handleCheckoutClick}
      />
    );
  };

  // =========================================================
  // PRODUCT CAROUSEL WITHOUT OFFER
  // =========================================================

  const renderProductCarouselWithoutOffer = (s: any) => {
    const rawProducts = s?.data?.length ? s.data : data.WithoutOffersFinal;

    const mappedProducts = rawProducts?.map((p: any) => ({
      ProductId: p.productId ?? p.ProductId,

      Name: p.name ?? p.Name,

      ImageUrl: p.imageUrl ?? p.ImageUrl,

      Price: p.price ?? p.Price,

      salePrice: p.salePrice ?? p.SalePrice,

      InStock: p.inStock ?? p.InStock ?? true,

      RemainingSaleSeconds:
        p.RemainingSaleSeconds ?? p.remainingSaleSeconds ?? 0,

      ItemType: p.ItemType ?? p.itemType,

      RedirectUrl: p.RedirectUrl ?? p.redirectUrl,

      MarketingTexts: p.MarketingTexts ?? p.marketingTexts ?? [],

      Rating: p.Rating ?? p.rating ?? 0,

      // IMPORTANT
      Images: p.Images ?? p.images ?? [],
    }));
    return (
      <ProductCarouselSection
        title="⚡ Flash Deals ⚡"
        products={mappedProducts}
        showLogo={false}
        handleProductPage={handlers.onProductClick}
        handleViewAllClick={
          handlers.onViewAllFlashDealsClick
        }
        handleCheckoutClick={handlers.handleCheckoutClick}
      />
    );
  };

  // =========================================================
  // VIDEO BANNERS
  // =========================================================

  const renderVideoBanners = (s: any) => (
    <VideoCarousel
      items={s?.data?.length ? s.data : data.CatToysFinal}
      handleViewAll={handlers.onViewAllClick}
      layout="overlay"
      title={
        <span>
          Shop <span className="text-[#2F5E2E] font-black">Pet Favorites</span>{" "}
          in Action
        </span>
      }
      description="Watch how our furry friends enjoy their favorite treats and toys live."
    />
  );

  // =========================================================
  // COMPONENT MAP
  // =========================================================

  const renderComponentMap: Record<
    string,
    (sectionObj?: any) => React.JSX.Element | null
  > = {
    // -------------------------------------------------------
    // CATEGORY SLIDESHOW
    // -------------------------------------------------------

    CategorySlideshow: (s) => {
      if (!s?.data?.length) {
        return null;
      }

      const adaptedCategories = s.data.map((cat: any) => {
        const item = {
          id: String(cat.id ?? cat.Id ?? ""),

          name: cat.name ?? cat.Name ?? "Category",

          title: cat.title ?? cat.Title ?? cat.name ?? cat.Name ?? "Category",

          imageUrl:
            cat.imageUrl ?? cat.ImageUrl ?? cat.image ?? cat.Image ?? "",

          image: cat.image ?? cat.Image ?? cat.imageUrl ?? cat.ImageUrl ?? "",

          /*
           * IMPORTANT:
           * Preserve custom content information.
           */
          itemType: cat.itemType ?? cat.ItemType ?? "",

          redirectUrl: cat.redirectUrl ?? cat.RedirectUrl ?? null,
        };

        // console.log("[ProductsTab] CategorySlideshow mapped item:", item);

        return item;
      });

      // console.log(
      //   "[ProductsTab] CategorySlideshow final items:",
      //   adaptedCategories,
      // );

      return (
        <CategorySlideshow
          items={adaptedCategories}
          onCategoryClick={
            handlers.onCategorySlideClick ?? handlers.onCategoryClick
          }
        />
      );
    },

    // -------------------------------------------------------
    // PRODUCT CAROUSELS
    // -------------------------------------------------------

    ProductCarouselWithOffer: renderProductCarouselWithOffer,

    "ProductCarouselSection-WithOffer-One": renderProductCarouselWithOffer,

    ProductCarouselWithoutOffer: renderProductCarouselWithoutOffer,

    "ProductCarouselSection-WithoutOffer": renderProductCarouselWithoutOffer,

    // -------------------------------------------------------
    // VIDEO BANNERS
    // -------------------------------------------------------

    VideoBanners: renderVideoBanners,

    "VideoBanners-One": renderVideoBanners,

    // -------------------------------------------------------
    // MAIN CAROUSEL
    // -------------------------------------------------------

    MainCarousel: (s) => {
      if (!s?.data?.length) {
        return null;
      }

      return (
        <div className="space-y-4">
          {/* <SectionHeader section={s} /> */}

          <Carousel
            items={s.data}
            onItemClick={(item) => handlers.onBannerClick?.(item)}
          />
        </div>
      );
    },

    // -------------------------------------------------------
    // PEEK CAROUSEL
    // -------------------------------------------------------

    PeekCarousel: (s) => {
      if (!s?.data?.length) {
        return null;
      }

      const carouselItems = s.data
        .map((item: any) => ({
          id: item.id ?? item.Id ?? null,

          title: item.title ?? item.Title ?? "",

          imageUrl:
            item.imageUrl ??
            item.ImageUrl ??
            item.mobileImageUrl ??
            item.MobileImageUrl ??
            null,

          redirectUrl: item.redirectUrl ?? item.RedirectUrl ?? null,

          raw: item,
        }))
        .filter((item: any) => Boolean(item.imageUrl) && Boolean(item.id));

      if (!carouselItems.length) {
        return null;
      }

      return (
        <div className="space-y-4">
          {/* <SectionHeader section={s} /> */}

          <PeekCarousel
            images={carouselItems.map((item: any) => ({
              id: Number(item.id),
              imageUrl: item.imageUrl,
            }))}
            handleImageClick={(index) => {
              const clickedItem = carouselItems[index];

              if (!clickedItem) {
                return;
              }

              if (clickedItem.raw.ItemType === "Custom") {
                window.location.href = clickedItem.raw.RedirectUrl;

                return;
              }

              handlers.handlePeekCarouselClick(clickedItem.id, clickedItem.raw);
            }}
          />
        </div>
      );
    },

    // -------------------------------------------------------
    // STRIP BANNER
    // -------------------------------------------------------

    // -------------------------------------------------------
    // STRIP BANNER
    // -------------------------------------------------------
    //
    // StripBanner supports two layouts:
    //
    //   Large → standard promotional strip
    //   Small → compact promotional strip
    //
    // The layout is controlled by the section's
    // LayoutType coming from the backend.
    //
    // The actual banner content comes from:
    //
    //   s.data[0]
    //
    // -------------------------------------------------------

    StripBanner: (s) => {
      // -----------------------------------------------------
      // No banner data
      // -----------------------------------------------------

      if (!s?.data?.length) {
        return null;
      }

      // -----------------------------------------------------
      // StripBanner normally contains one banner.
      // -----------------------------------------------------

      const banner = s.data[0];

      // -----------------------------------------------------
      // Resolve image URL
      // -----------------------------------------------------

      const imageUrl =
        banner?.imageUrl ??
        banner?.ImageUrl ??
        banner?.bannerUrl ??
        banner?.BannerUrl ??
        "";

      // -----------------------------------------------------
      // Resolve banner title
      //
      // Used as image alt text only.
      // We intentionally DO NOT display the section name
      // above the banner.
      // -----------------------------------------------------

      const bannerTitle =
        banner?.title ?? banner?.Title ?? "Promotional Banner";

      // -----------------------------------------------------
      // Resolve layout
      //
      // Backend:
      //
      // StripSmall  -> small
      // StripMedium -> medium
      // StripLarge  -> large
      // -----------------------------------------------------

      const layoutType = s?.layoutType?.toLowerCase();

      const bannerSize: "small" | "medium" | "large" =
        layoutType === "stripsmall"
          ? "small"
          : layoutType === "stripmedium"
            ? "medium"
            : "large";

      // -----------------------------------------------------
      // Resolve Sponsored state
      //
      // Backend now controls whether the Sponsored badge
      // should be displayed.
      // -----------------------------------------------------

      const isSponsored = banner?.isSponsored ?? banner?.IsSponsored ?? false;

      const sponsoredText =
        banner?.sponsoredText ?? banner?.SponsoredText ?? "Sponsored";

      // -----------------------------------------------------
      // Banner click
      //
      // Custom URL takes priority.
      // -----------------------------------------------------

      const handleStripBannerClick = () => {
        const redirectUrl =
          banner?.customUrl ??
          banner?.CustomUrl ??
          banner?.redirectUrl ??
          banner?.RedirectUrl ??
          "";

        if (redirectUrl) {
          window.open(redirectUrl, "_blank", "noopener,noreferrer");
          return;
        }

        // Existing application navigation
        handlers.onBannerClick?.(banner);
      };

      return (
        <StripBanner
          bannerId={Number(banner?.id ?? banner?.Id)}
          imageUrl={imageUrl}
          alt={bannerTitle}
          size={bannerSize}
          isSponsored={banner?.isSponsored ?? banner?.IsSponsored ?? false}
          sponsoredText={
            banner?.sponsoredText ?? banner?.SponsoredText ?? "Sponsored"
          }
          onClick={handleStripBannerClick}
        />
      );
    },
    // -------------------------------------------------------
    // BRANDS
    // -------------------------------------------------------
    BrandsSlider: (s) => {
      if (!s?.data?.length) {
        return null;
      }

      const adaptedBrands = s.data.map((b: any) => ({
        id: b.id ?? b.Id,
        name: b.name ?? b.Name ?? b.title ?? b.Title,
        logoUrl:
          b.LogoUrl ??
          b.logoUrl ??
          b.ImageUrl ??
          b.imageUrl ??
          "https://via.placeholder.com/150",
        redirectUrl: b.RedirectUrl ?? b.redirectUrl ?? b.link ?? b.Link,
        itemType: b.ItemType ?? b.itemType,
        itemId: b.ItemId ?? b.itemId ?? b.id ?? b.Id,
      }));

      return (
        <div className="space-y-4">
          {/* <SectionHeader section={s} /> */}

          <TopBrandsSection
            items={adaptedBrands}
            contentType={s.contentType}
            sectionTitle={s.sectionName || s.title}
            onBrandClick={handlers.onBrandClick}
          />
        </div>
      );
    },

    // -------------------------------------------------------
    // PET PROMO BANNER
    // -------------------------------------------------------

    PetPromoBanner: (s) => {
      if (!s?.data?.length) {
        return null;
      }

      const banner = s.data[0];

      return (
        <div className="space-y-4">
          {/* <SectionHeader section={s} /> */}

          <div className="rounded-2xl overflow-hidden border border-amber-200/60 shadow-3xs">
            <PetPromoBanner
              bannerId={Number(banner?.id ?? banner?.Id)}
              imageUrl={banner.imageUrl || banner.ImageUrl}
              onClick={() => handlers.onBannerClick?.(banner)}
              sponsoredText="Sponsored Campaign"
            />
          </div>
        </div>
      );
    },

    // -------------------------------------------------------
    // WAVES SECTION
    // -------------------------------------------------------

    WavesSection: (s) => {
      if (!s?.data?.length) {
        return null;
      }

      const mappedProducts = s.data?.map((p: any) => ({
        ProductId: p.productId ?? p.ProductId,

        Name: p.name ?? p.Name,

        ImageUrl: p.imageUrl ?? p.ImageUrl,

        Price: p.price ?? p.Price,

        salePrice: p.salePrice ?? p.SalePrice,

        InStock: p.inStock ?? p.InStock ?? true,

        RemainingSaleSeconds:
          p.RemainingSaleSeconds ?? p.remainingSaleSeconds ?? 0,

        ItemType: p.ItemType ?? p.itemType,

        RedirectUrl: p.RedirectUrl ?? p.redirectUrl,

        MarketingTexts: p.MarketingTexts ?? p.marketingTexts ?? [],

        Rating: p.Rating ?? p.rating ?? 0,

        // IMPORTANT
        Images: p.Images ?? p.images ?? [],
      }));

      const sectionTitle = s?.config?.Title || "Best Selling Toys";

      return (
       <div className="px-4 bg-gradient-to-b from-emerald-50/40 to-transparent py-2 rounded-b-[3rem] overflow-hidden">
          {/* <SectionHeader section={s} /> */}

          <WavesSection className="px-2">
            <ProductCarouselSection
              title={sectionTitle}
              handleProductPage={handlers.onProductClick}
              products={mappedProducts}
              showLogo={false}
              handleViewAllClick={handlers.onViewAllFlashDealsClick}
              handleCheckoutClick={handlers.handleCheckoutClick}
            />
          </WavesSection>
        </div>
      );
    },

    // -------------------------------------------------------
    // CATEGORY SLIDER
    // -------------------------------------------------------

    CategorySlider: (s) => {
      if (!s?.data?.length) {
        return null;
      }

      const adaptedCategories = s.data.map((cat: any) => ({
        id: String(cat.id ?? cat.Id),

        name: cat.name ?? cat.Name ?? "Category",

        title: cat.title ?? cat.name ?? cat.Name ?? "Category",

        imageUrl: cat.imageUrl ?? cat.ImageUrl ?? cat.image ?? cat.Image ?? "",

        image: cat.image ?? cat.Image ?? cat.imageUrl ?? cat.ImageUrl ?? "",

        itemType: cat.itemType ?? cat.ItemType,

        redirectUrl: cat.redirectUrl ?? cat.RedirectUrl ?? null,
      }));

      return (
        <div ref={saverSaleRef} className="space-y-2">
          {/* <SectionHeader section={s} /> */}

          <CategorySlider
            items={adaptedCategories}
            onCategoryClick={handlers.onCategoryClick}
          />
        </div>
      );
    },

    // -------------------------------------------------------
    // PET BANNER
    // -------------------------------------------------------

    PetBanner: (s) => {
      if (!s?.data?.length) {
        return null;
      }

      const banner = s.data[0];

      return (
        <div className="space-y-4">
          {/* <SectionHeader section={s} /> */}

          <PetBanner
            bannerPayload={banner}
            handleShopNow={() => handlers.onBannerClick?.(banner)}
          />
        </div>
      );
    },

    // -------------------------------------------------------
    // MULTI BANNER
    // -------------------------------------------------------

    MultiBanner: (s) => {
      if (!s?.data?.length) {
        return null;
      }

      const adaptedBanners = s.data.map((banner: any) => ({
        id: String(banner.id ?? banner.Id),

        src:
          banner.imageUrl ??
          banner.ImageUrl ??
          banner.bannerUrl ??
          banner.BannerUrl ??
          "https://via.placeholder.com/1200x400",

        type: (banner.type ?? banner.Type ?? "image").toLowerCase(),

        title: banner.title ?? banner.Title ?? "",

        subtitle: banner.subtitle ?? banner.Subtitle ?? "",

        redirectUrl: banner.redirectUrl ?? banner.RedirectUrl ?? "",

        sequence: banner.sequence ?? banner.Sequence ?? 0,

        // IMPORTANT:
        // Keep the original backend object.
        raw: banner,
      }));

      return (
        <div className="space-y-4">
          {/* <SectionHeader section={s} />  */}

          <MultiBanner
            banners={adaptedBanners}
            onBannerClick={(banner: any) => {
              // Send the ORIGINAL backend banner to the existing
              // navigation handler.
              handlers.onBannerClick?.(banner.raw ?? banner);
            }}
          />
        </div>
      );
    },

    // -------------------------------------------------------
    // FEATURE HIGHLIGHTS
    // -------------------------------------------------------

    FeatureHighlights: (s) => (
      <div className="space-y-4">
        <SectionHeader section={s} />

        <FeatureHighlights />
      </div>
    ),
  };

  // =========================================================
  // FALLBACK ORDER
  // =========================================================

  const defaultLocalOrder = [
    "CategorySlideshow",
    "ProductCarouselWithOffer",
    "PeekCarousel",
    "StripBanner",
    "BrandsSlider",
    "PetPromoBanner",
    "WavesSection",
    "CategorySlider",
    "MultiBanner",
    "ProductCarouselWithoutOffer",
    "PetBanner",
    "VideoBanners",
    "FeatureHighlights",
  ];

  // =========================================================
  // SERVER-DEFINED DISPLAY ORDER
  // =========================================================

  const renderingSequence =
    orderedSections.length > 0
      ? [...orderedSections]
          .filter((section) => section.isVisible !== false)
          .sort(
            (a, b) => Number(a.displayOrder ?? 0) - Number(b.displayOrder ?? 0),
          )
      : defaultLocalOrder.map((sectionType, index) => ({
          sectionId: `default-${sectionType}`,

          sectionType,

          sectionName: sectionType,

          displayOrder: index + 1,

          data: null,
        }));

  // =========================================================
  // DEBUG SECTION ORDER
  // =========================================================

  // console.table(
  //   renderingSequence.map((s: any, index: number) => ({
  //     renderIndex: index + 1,

  //     sectionId: s.sectionId,

  //     sectionType: s.sectionType,

  //     sectionName: s.sectionName,

  //     displayOrder: s.displayOrder,

  //     dataCount: Array.isArray(s.data) ? s.data.length : 0,
  //   })),
  // );

  // =========================================================
  // FINAL RENDER
  // =========================================================

  return (
   <div className="w-full max-w-full overflow-x-hidden space-y-6">
      {renderingSequence.map((section) => {
        const renderer = renderComponentMap[section.sectionType];

        if (!renderer) {
    

          return null;
        }

        return (
          <div
            key={section.sectionId}
            data-section-id={section.sectionId}
            data-display-order={section.displayOrder}
            data-section-type={section.sectionType}
          >
            {renderer(section)}
          </div>
        );
      })}
      <GoogleReviewsCarousel placeId="ChIJWTRemTliXz4Rbqad9gAaJc0" />
    </div>
  );
};
